import { useState, useEffect, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  Inbox, Eye, FileSignature, Send, Search, Download, Mail, Trash2, Loader2,
  CheckCircle2, XCircle, FileText, Square, CheckSquare, X, Filter, Sparkles,
  PenTool, AlertCircle,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import api from '../lib/api';

// === KANBAN CONFIG ===
const COLUMNS = [
  { id: 'ricevuto',       label: 'Ricevuti',       icon: Inbox,         accent: 'from-blue-500 to-cyan-500',     bg: 'bg-blue-50',    border: 'border-blue-200' },
  { id: 'da_revisionare', label: 'Da Revisionare', icon: Eye,           accent: 'from-amber-500 to-orange-500',  bg: 'bg-amber-50',   border: 'border-amber-200' },
  { id: 'firmato',        label: 'Firmati',        icon: FileSignature, accent: 'from-violet-500 to-indigo-500', bg: 'bg-violet-50',  border: 'border-violet-200' },
  { id: 'inviato',        label: 'Inviati',        icon: Send,          accent: 'from-emerald-500 to-green-500', bg: 'bg-emerald-50', border: 'border-emerald-200' },
];

export default function TestSubmissionsKanbanPage() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ by_status: {}, totale: 0, superati: 0, non_superati: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterPassed, setFilterPassed] = useState(''); // '' | 'passed' | 'failed'
  const [draggedId, setDraggedId] = useState(null);
  const [hoverCol, setHoverCol] = useState(null);
  const [selected, setSelected] = useState(new Set());

  // Send dialog
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [emailsInput, setEmailsInput] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  // Bulk download progress
  const [downloading, setDownloading] = useState(false);

  // Stato firma del docente loggato
  const [signatureStatus, setSignatureStatus] = useState({ has_signature: false, has_stamp: false, full_name: '' });
  const [signing, setSigning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [iRes, sRes, sigRes] = await Promise.all([
        api.get('/test-submissions'),
        api.get('/test-submissions/stats'),
        api.get('/test-submissions/my-signature-status'),
      ]);
      setItems(Array.isArray(iRes.data) ? iRes.data : []);
      setStats(sRes.data || stats);
      setSignatureStatus(sigRes.data || { has_signature: false });
    } catch (e) {
      toast.error('Errore caricamento test ricevuti');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  // === Filtering ===
  const filtered = useMemo(() => {
    return items.filter(it => {
      if (search) {
        const q = search.toLowerCase();
        const hit = (it.candidate_name || '').toLowerCase().includes(q)
                 || (it.test_title || '').toLowerCase().includes(q)
                 || (it.candidate_email || '').toLowerCase().includes(q)
                 || (it.candidate_fiscal_code || '').toLowerCase().includes(q);
        if (!hit) return false;
      }
      if (filterPassed === 'passed' && !it.passed) return false;
      if (filterPassed === 'failed' && it.passed) return false;
      return true;
    });
  }, [items, search, filterPassed]);

  const byCol = useMemo(() => {
    const map = { ricevuto: [], da_revisionare: [], firmato: [], inviato: [] };
    filtered.forEach(it => {
      const k = it.kanban_status || 'ricevuto';
      (map[k] || map.ricevuto).push(it);
    });
    return map;
  }, [filtered]);

  // === Drag & drop ===
  const onDragStart = (id) => setDraggedId(id);
  const onDragEnd = () => { setDraggedId(null); setHoverCol(null); };
  const onDragOver = (e, col) => { e.preventDefault(); setHoverCol(col); };
  const onDrop = async (e, col) => {
    e.preventDefault();
    setHoverCol(null);
    if (!draggedId) return;
    const sub = items.find(i => i.id === draggedId);
    if (!sub || (sub.kanban_status || 'ricevuto') === col) { setDraggedId(null); return; }
    // optimistic
    setItems(arr => arr.map(i => i.id === draggedId ? { ...i, kanban_status: col } : i));
    setDraggedId(null);
    try {
      await api.patch(`/test-submissions/${sub.id}/status`, { status: col });
      api.get('/test-submissions/stats').then(r => setStats(r.data));
      if (col === 'firmato') toast.success('✓ Firmato dal docente');
      else if (col === 'inviato') toast.success('📤 Marcato come inviato');
    } catch (err) {
      toast.error('Errore cambio stato');
      load();
    }
  };

  // === Selection ===
  const toggleSelect = (id, e) => {
    e?.stopPropagation();
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const selectAllInCol = (col) => {
    const ids = (byCol[col] || []).map(i => i.id);
    setSelected(prev => {
      const next = new Set(prev);
      const allSelected = ids.every(id => next.has(id));
      if (allSelected) ids.forEach(id => next.delete(id));
      else ids.forEach(id => next.add(id));
      return next;
    });
  };
  const clearSelection = () => setSelected(new Set());

  // === Actions ===
  const handleDownloadOne = async (sub) => {
    try {
      const res = await api.get(`/test-submissions/${sub.id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      const safeName = (sub.candidate_name || 'candidato').replace(/\s+/g, '_');
      a.download = `Test_${safeName}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Errore download PDF');
    }
  };

  const handleGenerateEml = async (sub) => {
    const recipientEmail = window.prompt(`Email destinatario (ente formazione) per ${sub.candidate_name}:`);
    if (!recipientEmail) return;
    const enteNome = window.prompt('Nome dell\'ente di formazione (per il saluto nel corpo):', '') || '';
    try {
      const res = await api.get(`/test-submissions/${sub.id}/eml`, {
        params: { recipient_email: recipientEmail, ente_nome: enteNome },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'message/rfc822' }));
      const a = document.createElement('a');
      a.href = url;
      const safeName = (sub.candidate_name || 'candidato').replace(/\s+/g, '_');
      a.download = `Email_${safeName}.eml`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Email .eml scaricata — apri il file con Outlook per inviarla come bozza');
    } catch (e) {
      toast.error('Errore generazione email: ' + (e?.response?.data?.detail || e.message));
    }
  };

  const handleBulkDownload = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) {
      toast.error('Seleziona almeno un test');
      return;
    }
    setDownloading(true);
    try {
      const res = await api.post('/test-submissions/bulk-download', { ids }, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/zip' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Test_Submissions_${new Date().toISOString().slice(0, 10)}.zip`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`📦 ZIP scaricato (${ids.length} test)`);
    } catch (e) {
      toast.error('Errore download ZIP');
    } finally {
      setDownloading(false);
    }
  };

  const handleBulkSign = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) {
      toast.error('Seleziona almeno un test');
      return;
    }
    if (!signatureStatus.has_signature) {
      toast.error('Firma autografa non caricata. Vai su Anagrafica Docenti e carica la tua firma.');
      return;
    }
    setSigning(true);
    try {
      const res = await api.post('/test-submissions/bulk-sign', { ids });
      toast.success(`✍️ ${res.data.signed} test vidimati con la tua firma`);
      setSelected(new Set());
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore vidima');
    } finally {
      setSigning(false);
    }
  };

  const openSendDialog = () => {
    if (selected.size === 0) {
      toast.error('Seleziona almeno un test');
      return;
    }
    setSubject(`Risultati Test di Formazione — ${selected.size} candidat${selected.size === 1 ? 'o' : 'i'}`);
    setMessage('');
    setSendDialogOpen(true);
  };

  const handleBulkEmail = async () => {
    const ids = Array.from(selected);
    const emails = emailsInput.split(/[,;]+/).map(s => s.trim()).filter(s => s.includes('@'));
    if (emails.length === 0) {
      toast.error('Inserisci almeno una email valida');
      return;
    }
    setSending(true);
    try {
      const res = await api.post('/test-submissions/bulk-email', {
        ids, emails, subject, message,
      });
      toast.success(`📧 Email inviata a ${emails.length} destinatari (${res.data.sent} test allegati)`);
      setSendDialogOpen(false);
      setEmailsInput('');
      setSelected(new Set());
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore invio email');
    } finally {
      setSending(false);
    }
  };

  const handleDelete = async (sub, e) => {
    e?.stopPropagation();
    if (!window.confirm(`Eliminare il test di "${sub.candidate_name}"?`)) return;
    try {
      await api.delete(`/test-submissions/${sub.id}`);
      setItems(arr => arr.filter(i => i.id !== sub.id));
      setSelected(prev => { const n = new Set(prev); n.delete(sub.id); return n; });
      toast.success('Eliminato');
    } catch (e) {
      toast.error('Errore eliminazione');
    }
  };

  return (
    <div className="space-y-4 relative" data-testid="test-submissions-kanban-page">
      {/* Header */}
      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
        className="flex justify-between items-start flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Inbox className="w-8 h-8 text-blue-600" /> Test Ricevuti dai Discenti
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Tutti i test compilati dai discenti via link pubblico. Trascina tra le colonne per cambiare stato, seleziona più test e scarica/invia in blocco.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {selected.size > 0 && (
            <>
              <Button variant="outline" onClick={clearSelection} size="sm" className="border-slate-300" data-testid="clear-selection-btn">
                <X className="w-4 h-4 mr-1" /> Deseleziona ({selected.size})
              </Button>
              <Button onClick={handleBulkSign} disabled={signing || !signatureStatus.has_signature} size="sm"
                className="bg-gradient-to-r from-violet-500 to-indigo-500 hover:from-violet-600 hover:to-indigo-600 disabled:opacity-50"
                title={!signatureStatus.has_signature ? 'Devi prima caricare la firma autografa' : `Vidima ${selected.size} test con la tua firma`}
                data-testid="bulk-sign-btn">
                {signing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <PenTool className="w-4 h-4 mr-1" />}
                Vidima con Firma ({selected.size})
              </Button>
              <Button onClick={handleBulkDownload} disabled={downloading} size="sm"
                className="bg-gradient-to-r from-indigo-500 to-violet-500 hover:from-indigo-600 hover:to-violet-600" data-testid="bulk-download-btn">
                {downloading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
                Scarica ZIP ({selected.size})
              </Button>
              {/* v88: pulsante "Invia all'Ente" rimosso (il docente invia manualmente) */}
            </>
          )}
        </div>
      </motion.div>

      {/* Alert firma mancante */}
      {!loading && !signatureStatus.has_signature && (
        <motion.div initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }}
          className="bg-amber-50 border-l-4 border-amber-400 p-3 rounded-r-lg flex items-start gap-3"
          data-testid="no-signature-alert">
          <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1 text-sm">
            <strong className="text-amber-900">Firma autografa non caricata.</strong>{' '}
            <span className="text-amber-800">
              Per poter vidimare i test (apporre la tua firma sui PDF) devi prima caricare la tua firma autografa.
              Vai su <strong>Anagrafica Docenti</strong> → cerca te stesso → carica la firma. La firma verrà poi inserita automaticamente sul PDF di ogni test che vidimi.
            </span>
          </div>
        </motion.div>
      )}

      {/* Stats row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={Sparkles} label="Totale ricevuti" value={stats.totale} accent="text-blue-600 bg-blue-50" />
        <StatCard icon={CheckCircle2} label="Test superati" value={stats.superati} accent="text-emerald-600 bg-emerald-50" />
        <StatCard icon={XCircle} label="Non superati" value={stats.non_superati} accent="text-red-600 bg-red-50" />
        <StatCard icon={CheckSquare} label="Selezionati" value={selected.size} accent="text-violet-600 bg-violet-50" />
      </div>

      {/* Filtri */}
      <div className="flex flex-wrap gap-2 items-center bg-white border rounded-lg p-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Cerca per nome candidato, test, email, CF..."
            className="pl-8 border-0 shadow-none focus-visible:ring-1"
            data-testid="search-submissions-input"
          />
        </div>
        <div className="flex items-center gap-1 text-xs">
          <Filter className="w-4 h-4 text-slate-400" />
          <button onClick={() => setFilterPassed('')}
            className={`px-2 py-1 rounded ${!filterPassed ? 'bg-slate-200' : 'hover:bg-slate-100'}`}>Tutti</button>
          <button onClick={() => setFilterPassed(filterPassed === 'passed' ? '' : 'passed')}
            className={`px-2 py-1 rounded flex items-center gap-1 ${filterPassed === 'passed' ? 'bg-emerald-100 text-emerald-800' : 'hover:bg-slate-100'}`}>
            <CheckCircle2 className="w-3 h-3" /> Superati
          </button>
          <button onClick={() => setFilterPassed(filterPassed === 'failed' ? '' : 'failed')}
            className={`px-2 py-1 rounded flex items-center gap-1 ${filterPassed === 'failed' ? 'bg-red-100 text-red-800' : 'hover:bg-slate-100'}`}>
            <XCircle className="w-3 h-3" /> Non superati
          </button>
        </div>
      </div>

      {/* Kanban */}
      {loading ? (
        <div className="text-center py-12 text-slate-500"><Loader2 className="w-8 h-8 mx-auto animate-spin mb-2" /> Caricamento test ricevuti...</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          {COLUMNS.map(col => {
            const colItems = byCol[col.id] || [];
            const Icon = col.icon;
            const isHover = hoverCol === col.id;
            const allSelInCol = colItems.length > 0 && colItems.every(i => selected.has(i.id));
            return (
              <div key={col.id}
                onDragOver={(e) => onDragOver(e, col.id)}
                onDrop={(e) => onDrop(e, col.id)}
                onDragLeave={() => setHoverCol(null)}
                className={`rounded-xl ${col.bg} ${col.border} border-2 ${isHover ? 'ring-2 ring-blue-400 scale-[1.02]' : ''} transition-all duration-200`}
                data-testid={`subcol-${col.id}`}
              >
                {/* Header colonna */}
                <div className={`bg-gradient-to-r ${col.accent} text-white p-3 rounded-t-xl flex justify-between items-center`}>
                  <div className="flex items-center gap-2">
                    <Icon className="w-4 h-4" />
                    <strong className="text-sm">{col.label}</strong>
                  </div>
                  <div className="flex items-center gap-2">
                    {colItems.length > 0 && (
                      <button
                        onClick={() => selectAllInCol(col.id)}
                        className="text-white/80 hover:text-white text-[10px] bg-white/10 hover:bg-white/20 px-1.5 py-0.5 rounded transition"
                        title={allSelInCol ? 'Deseleziona tutti' : 'Seleziona tutti'}
                        data-testid={`select-all-${col.id}`}
                      >
                        {allSelInCol ? 'Tutti ✓' : 'Tutti'}
                      </button>
                    )}
                    <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full" data-testid={`subcol-count-${col.id}`}>
                      {colItems.length}
                    </span>
                  </div>
                </div>
                {/* Cards */}
                <div className="p-2 space-y-2 min-h-[200px] max-h-[70vh] overflow-y-auto">
                  <AnimatePresence>
                    {colItems.map(sub => (
                      <SubmissionCard
                        key={sub.id}
                        sub={sub}
                        selected={selected.has(sub.id)}
                        onToggleSelect={(e) => toggleSelect(sub.id, e)}
                        onDragStart={onDragStart}
                        onDragEnd={onDragEnd}
                        isDragging={draggedId === sub.id}
                        onDownload={() => handleDownloadOne(sub)}
                        onGenerateEml={() => handleGenerateEml(sub)}
                        onDelete={(e) => handleDelete(sub, e)}
                      />
                    ))}
                  </AnimatePresence>
                  {colItems.length === 0 && (
                    <div className="text-center text-xs text-slate-400 py-6 italic">
                      Trascina qui i test da {col.label.toLowerCase()}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* === DIALOG: Invia all'ente di formazione === */}
      <Dialog open={sendDialogOpen} onOpenChange={setSendDialogOpen}>
        <DialogContent className="max-w-xl" data-testid="send-email-dialog">
          <DialogHeader>
            <DialogTitle>Invia all&apos;Ente di Formazione</DialogTitle>
            <DialogDescription>
              Verranno allegati {selected.size} PDF dei test selezionati. Lo stato verrà aggiornato automaticamente a &quot;Inviato&quot;.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Email destinatari (separate da virgola)</Label>
              <Input
                value={emailsInput}
                onChange={e => setEmailsInput(e.target.value)}
                placeholder="ente@formazione.it, segreteria@enteformazione.it"
                data-testid="emails-input"
              />
              <p className="text-[11px] text-slate-500 mt-1">L&apos;email partirà dal tuo account come mittente.</p>
            </div>
            <div>
              <Label>Oggetto</Label>
              <Input value={subject} onChange={e => setSubject(e.target.value)} data-testid="email-subject-input" />
            </div>
            <div>
              <Label>Messaggio (opzionale)</Label>
              <Textarea
                value={message}
                onChange={e => setMessage(e.target.value)}
                rows={4}
                placeholder="Buongiorno, in allegato i risultati del corso..."
                data-testid="email-message-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSendDialogOpen(false)}>Annulla</Button>
            <Button onClick={handleBulkEmail} disabled={sending || !emailsInput.trim()}
              className="bg-gradient-to-r from-emerald-500 to-green-500" data-testid="confirm-send-email-btn">
              {sending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
              Invia ora ({selected.size} PDF)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// === SUB-COMPONENTS ===
function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <motion.div whileHover={{ y: -2 }} className="bg-white border rounded-lg p-3 shadow-sm">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${accent}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <p className="text-xs text-slate-500">{label}</p>
          <p className="text-2xl font-bold leading-tight">{value || 0}</p>
        </div>
      </div>
    </motion.div>
  );
}

function SubmissionCard({ sub, selected, onToggleSelect, onDragStart, onDragEnd, isDragging, onDownload, onGenerateEml, onDelete }) {
  const passed = sub.passed;
  const submittedAt = sub.submitted_at ? new Date(sub.submitted_at).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';
  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: isDragging ? 0.3 : 1, scale: isDragging ? 0.95 : 1 }}
      exit={{ opacity: 0, scale: 0.8 }}
      transition={{ duration: 0.15 }}
      draggable
      onDragStart={() => onDragStart(sub.id)}
      onDragEnd={onDragEnd}
      whileHover={{ y: -2, boxShadow: '0 8px 20px rgba(0,0,0,0.08)' }}
      className={`bg-white rounded-lg border-l-4 ${passed ? 'border-l-emerald-500' : 'border-l-red-500'} ${selected ? 'ring-2 ring-blue-400' : ''} p-3 cursor-grab shadow-sm hover:shadow-md transition-shadow`}
      data-testid={`submission-${sub.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <button
          onClick={onToggleSelect}
          className="mt-0.5 flex-shrink-0 text-blue-600 hover:text-blue-700"
          title={selected ? 'Deseleziona' : 'Seleziona per invio/download'}
          data-testid={`select-${sub.id}`}
        >
          {selected ? <CheckSquare className="w-5 h-5" /> : <Square className="w-5 h-5 text-slate-300" />}
        </button>
        <div className="flex-1 min-w-0">
          <strong className="text-sm leading-snug block truncate">{sub.candidate_name || '—'}</strong>
          <p className="text-xs text-slate-500 truncate">{sub.test_title || '—'}</p>
        </div>
        <Badge className={`text-[10px] whitespace-nowrap ${passed ? 'bg-emerald-100 text-emerald-800 border-emerald-300' : 'bg-red-100 text-red-800 border-red-300'} border`}>
          {sub.score_pct}%
        </Badge>
      </div>
      <div className="flex items-center justify-between mt-2 text-[11px] text-slate-500">
        <span className="flex items-center gap-1">
          {passed ? <CheckCircle2 className="w-3 h-3 text-emerald-500" /> : <XCircle className="w-3 h-3 text-red-500" />}
          {passed ? 'Superato' : 'Non superato'}
        </span>
        <span>{submittedAt}</span>
      </div>
      {sub.candidate_email && (
        <p className="text-[10px] text-slate-400 truncate mt-1">{sub.candidate_email}</p>
      )}
      {sub.signed_by && (
        <div className="text-[10px] text-violet-700 mt-1 flex items-center gap-1 bg-violet-50 px-1.5 py-0.5 rounded">
          <PenTool className="w-3 h-3" /> Vidimato da {sub.signed_by}
          {sub.signature_image_path && <span className="text-emerald-600 font-bold">· con firma ✓</span>}
        </div>
      )}
      <div className="flex justify-end gap-1 mt-2 pt-2 border-t">
        <button
          onClick={(e) => { e.stopPropagation(); onDownload(); }}
          className="text-slate-500 hover:text-blue-600 p-1 rounded hover:bg-blue-50 transition"
          title="Scarica PDF"
          data-testid={`download-${sub.id}`}
        >
          <FileText className="w-4 h-4" />
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); onGenerateEml(); }}
          className="text-slate-500 hover:text-amber-600 p-1 rounded hover:bg-amber-50 transition"
          title="Genera email (.eml) pronta per Outlook"
          data-testid={`eml-${sub.id}`}
        >
          <Mail className="w-4 h-4" />
        </button>
        <button
          onClick={onDelete}
          className="text-slate-400 hover:text-red-600 p-1 rounded hover:bg-red-50 transition"
          title="Elimina"
          data-testid={`delete-${sub.id}`}
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </motion.div>
  );
}
